import React from 'react';
import {
  DefaultButton,
} from '@fluentui/react';
import Store from '../../shared/state/Store';
import UiReducer from '../../shared/state/UiReducer';
import ModellerReducer from '../ModellerReducer';
import Styles from '../Styles';
import ModellerDefaults from '../ModellerDefaults';
import { convertLegacyGraphics } from '../ModelPreview/cytoscape/GraphicsConverter';
import ModelUtils from '../ModelUtils';
import convertModel from '../../shared/util/apis';

export default ({ mode }) => {
  const [globalState, dispatch] = Store.useStore();
  const [button, setButton] = React.useState();
  const openModelCmpxRef = React.useRef();
  const convertAstCmpModelRef = React.useRef();

  const items = [
    {
      key: 'openModelCmpx',
      text: 'Open Model (CMPX)',
      disabled: mode === ModellerDefaults.OperationMode.ModellerWebApp,
      onClick: () => {
        if (openModelCmpxRef.current) {
          openModelCmpxRef.current.click();
        }
      },
    },

    {
      key: 'convertAstCmpModel',
      text: 'Convert AST/CMP model',
      disabled: mode === ModellerDefaults.OperationMode.ModellerWebApp,
      onClick: () => {
        if (convertAstCmpModelRef.current) {
          convertAstCmpModelRef.current.click();
        }
      },
    },

    {
      key: 'saveModelAs',
      text: 'Save model as',
      disabled: mode === ModellerDefaults.OperationMode.ModellerWebApp,
      onClick: () => {
        const model = ModellerReducer.getModel(globalState);
        const content = JSON.stringify({ model });
        const a = document.createElement('a');
        const file = new Blob([content], { type: 'file' });
        a.href = URL.createObjectURL(file);
        a.download = ModellerReducer.getSetting(globalState, ModellerReducer.SettingKeys.filename) || 'Saved Model.cmpx';
        a.click();
        URL.revokeObjectURL(a.href);
        a.remove();
      },
    },

    {
      key: 'modelProperties',
      text: 'Model Properties',
      disabled: !ModellerReducer.getModel(globalState),
      onClick: () => {
        dispatch(UiReducer.setGenericUiAttribute(ModellerDefaults.ComponentKeys.ModellerModelPropertiesPopupKey, true));
      },
    },
  ];

  return (
    <>
      <DefaultButton
        text="File"
        className={Styles.menuButton}
        menuProps={{
          className: Styles.menuItemList,
          calloutProps: {
            minPagePadding: 0,
          },
          items,
        }}
        onMenuClick={(event) => {
          setButton(event.target.closest('button'));
        }}
        onAfterMenuDismiss={() => {
          button.blur();
        }}
      />

      <input
        ref={openModelCmpxRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(event) => {
          dispatch(UiReducer.uiSetErrors());

          if (event.target.files?.length > 0) {
            // Check size
            // Set file selection
            const file = event.target.files[0];
            const { size, name } = file;
            dispatch(ModellerReducer.setSetting(ModellerReducer.SettingKeys.filename, name));

            if (size > 26214400) {
              dispatch(UiReducer.uiSetErrors(['File size too big, max 25 Mb allowed']));
              if (openModelCmpxRef.current) {
                openModelCmpxRef.current.value = '';
              }
              return;
            }

            const fileReaderText = new FileReader();
            fileReaderText.onload = (e) => {
              let errors;
              let newModel;
              try {
                newModel = JSON.parse(e.target.result).model;
                delete (newModel.riskTable);
              } catch (error) {
                console.log('Load error', error);
                const errorText = new Option(`${error}`).innerHTML;
                if (errorText.indexOf('not valid JSON') >= 0) {
                  errors = [
                    'Invalid file format.',
                    'Only CMPX is supported.',
                  ];
                } else {
                  errors = [
                    'Invalid format',
                    errorText,
                  ];
                }
              }

              if (!errors && !newModel?.networks) {
                errors = [
                  'Invalid file format.',
                  'Only CMPX is supported.',
                ];
              }

              if (errors) {
                dispatch(UiReducer.uiSetErrors(errors));
                return;
              }

              // Convert legacy graphics format if present
              if (newModel) {
                newModel = convertLegacyGraphics(newModel);
              }

              // console.log('New model', JSON.stringify(newModel, null, 2));

              dispatch(ModellerReducer.loadModel(newModel));
              const validationMessages = ModelUtils.validate(newModel);
              if (validationMessages.messages.length > 0) {
                dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GeneralSimpleMessageModal, {
                  title: 'Advisory',
                  messages: validationMessages.messages,
                }));
              }
            };
            fileReaderText.readAsText(file);
          } else {
            // No file selected, do nothing
          }
          if (openModelCmpxRef.current) {
            openModelCmpxRef.current.value = '';
          }
        }}
      />

      <input
        ref={convertAstCmpModelRef}
        type="file"
        style={{ display: 'none' }}
        onChange={async (event) => {
          dispatch(UiReducer.uiSetErrors());
          dispatch(UiReducer.uiSetLoading(true));

          const file = event.target.files[0];

          // if file is not a CMP or AST file, show error
          if (!file.name.toLowerCase().endsWith('.cmp') && !file.name.toLowerCase().endsWith('.ast')) {
            dispatch(UiReducer.uiSetErrors(['Only CMP and AST files are supported for conversion']));
            dispatch(UiReducer.uiSetLoading(false));
            if (convertAstCmpModelRef.current) {
              convertAstCmpModelRef.current.value = '';
            }
            return;
          }

          // Check that file size is less than 25 MB
          if (file.size > 25 * 1024 * 1024) {
            dispatch(UiReducer.uiSetErrors(['File size too big, max 25 Mb allowed']));
            dispatch(UiReducer.uiSetLoading(false));
            if (convertAstCmpModelRef.current) {
              convertAstCmpModelRef.current.value = '';
            }
            return;
          }

          const fileReaderBase64 = new FileReader();
          fileReaderBase64.onload = async (e) => {
            const modelFileBase64 = e.target.result.split(',')[1];
            const requestBody = {
              'sync-wait': true,
              name: file.name,
              keepGraphics: true,
              keepCaseData: true,
              file: modelFileBase64,
            };
            const response = await convertModel({ requestJson: requestBody, path: '/public/v1/tools/modeljson' });
            if (response.status === 'success') {
              const convertedModel = convertLegacyGraphics(response.results.model);
              dispatch(ModellerReducer.loadModel(convertedModel));
            } else {
              dispatch(UiReducer.uiSetErrors(response.messages));
            }

            dispatch(UiReducer.uiSetLoading(false));

            if (convertAstCmpModelRef.current) {
              convertAstCmpModelRef.current.value = '';
            }
          };
          fileReaderBase64.readAsDataURL(file);
        }}
      />
    </>
  );
};
